/**
 * focusJourney.js — the SUBJECT LEARNING JOURNEY behind Focus. ONE Next Action Resolver inside the chosen subject:
 *   same objective (next stage) → review due in subject (≤1 in 3 steps) → next objective (prereq gap first) → challenge → journey covered.
 * Never switches subject. Built on learningGraph (objectives+evidence), focusDay (question readiness), lessonStageProgress (Learn→…→Mastery).
 * Coverage ≠ mastery: explored (lesson opened) · practised (stage ≥ PRACTICE) · secure (mastered/confident) · review (due) are tracked separately.
 */
import { objectivesFor, activitiesFor } from "@/lib/learningGraph";
import { readyCodes, fetchReadyCodes } from "@/lib/focusDay";
import { getLessonStage, LESSON_STAGES } from "@/lib/lessonStageProgress";
import { track } from "@/lib/track";

export const JOURNEY_KEY = "mwq_focus_journey_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const lessonCache = new Map();
const lessonExists = (code) => { if (!lessonCache.has(code)) lessonCache.set(code, fetch(`${API}/lessons/${code}`).then((r) => r.ok).catch(() => false)); return lessonCache.get(code); };

export const loadJourney = () => { try { return JSON.parse(localStorage.getItem(JOURNEY_KEY) || "null"); } catch { return null; } };
export const saveJourney = (j) => { try { localStorage.setItem(JOURNEY_KEY, JSON.stringify(j)); } catch { /* ignore */ } return j; };
export const clearJourney = () => { try { localStorage.removeItem(JOURNEY_KEY); } catch { /* ignore */ } };

/** Start/continue a journey for subject+year (explicit learner choice is the only thing that changes subject). */
export function startJourney(subject, year) {
  const j = loadJourney();
  if (j && j.subject === subject && j.year === year) return j;
  return saveJourney({ subject, year, startedAt: new Date().toISOString(), steps: [] });
}
export function recordStep(subject, year, step) {
  const j = startJourney(subject, year);
  j.steps = [...(j.steps || []), { ...step, at: new Date().toISOString() }].slice(-200);
  return saveJourney(j);
}

const RETRIEVE = { maths: { to: "/times-tables-quest", label: "Quick Maths review" }, english: { to: "/english-quest", label: "Quick English review" } };
const STAGE_ORDER = ["learn", "practise", "apply", "check"];

/** Coverage/mastery model for one subject+year (child view + grown-up view come from the same numbers). */
export function journeyProgress(subject, year) {
  const objs = objectivesFor(subject, year, "");
  const explored = objs.filter((o) => getLessonStage(o.id) >= LESSON_STAGES.LEARN).length;
  const practised = objs.filter((o) => getLessonStage(o.id) >= LESSON_STAGES.PRACTICE || ["practising", "confident", "mastered"].includes(o.state)).length;
  const secure = objs.filter((o) => ["confident", "mastered"].includes(o.state)).length;
  const review = objs.filter((o) => o.state === "review").length;
  const notYet = objs.filter((o) => o.state === "not_assessed" && getLessonStage(o.id) < LESSON_STAGES.LEARN).length;
  const steps = (loadJourney()?.subject === subject ? loadJourney().steps : []) || [];
  return { total: objs.length, explored, practised, secure, review, notYet, steps: steps.length, covered: objs.length ? Math.round((explored / objs.length) * 100) : 0, securePct: objs.length ? Math.round((secure / objs.length) * 100) : 0 };
}

/** Internal content-health state for an objective (never shown to children). */
export function contentHealth(o, ready = readyCodes(), hasLesson = true) {
  if (!hasLesson) return "MISSING_ACTIVITY";
  const q = !!(ready && ready.has(o.id));
  const games = activitiesFor(o.subject).length > 0 || RETRIEVE[o.subject];
  if (!q) return "MISSING_ASSESSMENT";
  if (!games) return "INSUFFICIENT_VARIETY";
  return "READY";
}

/** Which stage should this objective get next? Learn → Practise → Apply → Check, driven by lesson stage + mastery evidence. */
function nextStage(o, lastKind, ready) {
  const q = !!(ready && ready.has(o.id));
  const hasApply = !!(activitiesFor(o.subject)[0] || RETRIEVE[o.subject]);
  if (lastKind === "learn") return q ? "practise" : hasApply ? "apply" : "check";
  if (lastKind === "practise") return hasApply ? "apply" : q ? "check" : "learn";
  if (lastKind === "apply") return q ? "check" : "learn";
  if (lastKind === "review") return "learn";
  return getLessonStage(o.id) < LESSON_STAGES.LEARN ? "learn" : q ? "practise" : "learn";
}

function routeFor(o, kind, ready) {
  const q = !!(ready && ready.has(o.id));
  if (kind === "learn") return { to: `/lesson/${o.id}`, e: "📚", label: "Learn" };
  if (kind === "practise") return q ? { to: `/practice/${o.id}`, e: "✏️", label: "Practise" } : { to: `/lesson/${o.id}`, e: "📚", label: "Learn" };
  if (kind === "check") return q ? { to: `/learn-topics/${o.id}/test`, e: "✅", label: "Check" } : { to: `/lesson/${o.id}`, e: "📚", label: "Learn" };
  const acts = activitiesFor(o.subject).filter((x) => !x.twoPlayer);
  const n = (loadJourney()?.steps || []).filter((s) => s.kind === "apply").length;
  const g = acts.length ? acts[n % acts.length] : null; // rotate activities → variety
  if (g && (n % 3 !== 2 || !RETRIEVE[o.subject])) return { to: g.to, e: "🎮", label: g.label };
  if (RETRIEVE[o.subject]) return { to: RETRIEVE[o.subject].to, e: "🧮", label: RETRIEVE[o.subject].label };
  return { to: `/lesson/${o.id}`, e: "📚", label: "Learn" };
}

/**
 * Next action INSIDE the subject. `after` = objective code just finished, `kind` = what was just done.
 * Returns { kind: CONTINUE_OBJECTIVE|REVIEW|NEXT_OBJECTIVE|SUPPORT|CHALLENGE|JOURNEY_COVERED, to, title, subtitle, objective, stage }.
 */
export async function nextInSubject(subject, year, { after = null, kind: lastKind = null } = {}) {
  const ready = (await fetchReadyCodes()) || readyCodes();
  const objs = objectivesFor(subject, year, "");
  const j = startJourney(subject, year);
  const recent = (j.steps || []).slice(-3);
  const done = (o) => o.state === "mastered" || (o.state === "confident" && getLessonStage(o.id) >= LESSON_STAGES.MASTERY);
  const cur = after ? objs.find((o) => o.id === after) : null;
  // 1 · same objective, next stage (do not leave an objective after one lesson)
  const sameRun = (j.steps || []).slice(-4).filter((s) => s.code === after).length;
  if (cur && !done(cur) && lastKind && lastKind !== "check" && sameRun < 4) {
    const st = nextStage(cur, lastKind, ready); const r = routeFor(cur, st, ready);
    return { kind: cur.state === "review" || cur.state === "practising" ? "SUPPORT" : "CONTINUE_OBJECTIVE", to: r.to, title: cur.label, subtitle: `${r.e} ${r.label} · same skill`, objective: cur.id, stage: st };
  }
  // 2 · spaced review inside the subject — at most one in every three steps
  const due = objs.find((o) => o.state === "review" && o.id !== after);
  if (due && !recent.some((s) => s.kind === "review")) { const r = routeFor(due, "practise", ready); return { kind: "REVIEW", to: r.to, title: due.label, subtitle: "↻ Quick review · keeps it remembered", objective: due.id, stage: "review" }; }
  // 3 · next objective in syllabus order (practising first = prerequisite gap, then unexplored)
  const order = [...objs].sort((a, b) => ({ practising: 0, review: 1, not_assessed: 2, confident: 3, mastered: 4 }[a.state] - { practising: 0, review: 1, not_assessed: 2, confident: 3, mastered: 4 }[b.state]));
  const checked = new Set((j.steps || []).filter((s) => s.kind === "check" || s.kind === "review").map((s) => s.code));
  for (const o of order) {
    if (o.id === after || done(o)) continue;
    if (checked.has(o.id) && !["review", "practising"].includes(o.state)) continue; // already checked this journey — evidence decides if it returns as review
    if (!(await lessonExists(o.id))) continue;
    const st = nextStage(o, null, ready); const r = routeFor(o, st, ready);
    return { kind: o.state === "practising" ? "SUPPORT" : "NEXT_OBJECTIVE", to: r.to, title: o.label, subtitle: `${r.e} ${r.label} · next step`, objective: o.id, stage: st };
  }
  // 4 · everything secure → challenge within the subject, then covered
  const g = activitiesFor(subject)[0] || (RETRIEVE[subject] && { to: RETRIEVE[subject].to, label: RETRIEVE[subject].label });
  if (g && objs.length && lastKind !== "challenge") return { kind: "CHALLENGE", to: g.to, title: g.label, subtitle: "🏆 Challenge · same subject", stage: "challenge" };
  return { kind: "JOURNEY_COVERED", to: null, title: `${subject} journey`, subtitle: "You've worked through your journey for this year." };
}

/** Child-facing progress line (no codes). */
export function journeyLine(subject, year, label) {
  const p = journeyProgress(subject, year);
  if (!p.steps) return null;
  return `🌟 ${label} journey · step ${p.steps + 1}${p.secure ? ` · ${p.secure} skill${p.secure === 1 ? "" : "s"} secure` : ""}`;
}

export function trackJourney(subject, r) { track("focus_journey_next", { subject, kind: r.kind, stage: r.stage || "" }); }

const SUBJ_KEY = { maths: "maths", mathematics: "maths", math: "maths", english: "english", "english & reading": "english", literacy: "english", science: "science", history: "history", geography: "geography" };
export const subjectKey = (name) => SUBJ_KEY[String(name || "").toLowerCase()] || (/-ma-/.test(name) ? "maths" : /-en-|-lit-/.test(name) ? "english" : /-sc-/.test(name) ? "science" : /-hi-/.test(name) ? "history" : /-geo-/.test(name) ? "geography" : null);
/** If an active journey owns this content, the "Next" route stays inside the subject; otherwise null (caller keeps its default). */
export function journeyNextFor(code, subjectName, kind) {
  const j = loadJourney(); if (!j || !(j.steps || []).length) return null;
  const k = subjectKey(subjectName) || subjectKey(code);
  if (k !== j.subject) return null;
  return `/start/${j.subject}?y=${j.year}&after=${encodeURIComponent(code)}&kind=${kind}`;
}
